import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import endpoints from "../../endpoint";
import { Upload } from "tus-js-client";

export const addCourse = createAsyncThunk(
  "course/addCourse",
  async ({ courseData, onSuccess }, { rejectWithValue }) => {
    const token = sessionStorage.getItem("token");

    try {
      // Image Upload
      const imageUploadResponse = await axios.post(
        `${endpoints.serverBaseUrl}api/generate-presigned-url`,
        {
          fileName: courseData.image.name,
          fileType: courseData.image.type,
        },
        { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }
      );

      if (!imageUploadResponse.data.presignedUrl) {
        throw new Error("Failed to get presigned URL for image upload.");
      }

      const {
        presignedUrl: imagePresignedUrl,
        publicId: imagePublicId,
        cloudflareBaseUrl,
      } = imageUploadResponse.data;

      await axios.put(imagePresignedUrl, courseData.image, {
        headers: { "Content-Type": courseData.image.type },
      });

      const imageUrl = `${cloudflareBaseUrl}/${imagePublicId}`;

      // Video Upload
      const videoUpload = new Upload(courseData.previewVideo, {
        endpoint: `${endpoints.serverBaseUrl}api/generate-upload-url`,
        retryDelays: [0, 3000, 5000, 10000],
        chunkSize: 15 * 1024 * 1024, // 15MB chunks
        metadata: { name: courseData.previewVideo.name },

        onError: (error) => {
          console.error("Upload Error:", error);
          alert("Video upload failed. Please try again.");
          return rejectWithValue("Video upload failed.");
        },

        onSuccess: async () => {
          console.log("Video upload successful!");
          const uploadURL = videoUpload.url;
          const videoId = uploadURL.split("/tus/")[1].split("?")[0];

          const previewVideoUrl = "";

          // Prepare the final course payload
          const discountedPrice = (
            courseData.price -
            (courseData.price * courseData.discount) / 100
          ).toFixed(0);

          const coursePayload = {
            title: courseData.title,
            description: courseData.description,
            price: courseData.price,
            category: courseData.category,
            previewVideo: previewVideoUrl,
            previewVideoPublicId: videoId,
            discount: courseData.discount,
            discountedPrice,
            createdBy: courseData.createdBy,
            image: imageUrl,
            imagePublicId: imagePublicId,
          };

          try {
            const response = await axios.post(
              `${endpoints.serverBaseUrl}api/course`,
              coursePayload,
              { headers: { Authorization: `Bearer ${token}` } }
            );

            if (onSuccess) {
              onSuccess();
            }

            return response.data;
          } catch (backendError) {
            console.error("Error creating course:", backendError);
            return rejectWithValue(
              backendError.response?.data || "Error creating the course"
            );
          }
        },

        onProgress: (bytesUploaded) => {
          const progress = (
            (bytesUploaded / courseData.previewVideo.size) *
            100
          ).toFixed(2);
          console.log("Video upload progress:", progress);
        },
      });

      // Start the video upload process
      videoUpload.start();
    } catch (error) {
      console.error("Error adding course:", error);
      return rejectWithValue(
        error.response?.data || "An error occurred while adding the course."
      );
    }
  }
);

// Course Slice
const courseSlice = createSlice({
  name: "course",
  initialState: {
    courses: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addCourse.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCourse.fulfilled, (state, action) => {
        state.loading = false;
        state.courses.push(action.payload);
      })
      .addCase(addCourse.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default courseSlice.reducer;
